
import { computed, defineComponent, ref, watch } from "vue";
import oidcManager from "../authentication/oidcManager";
import VersionHelper from "../helpers/VersionHelper";
import PermissionHelper from "../helpers/PermissionHelper";
import { useRouter } from "../router";
import licenseApi from "../api/license/licenseApi";
import tenantSettingApi from "../api/tenantSetting/tenantSettings";
import { licenseLevel } from "../model/LicenseLevel";
import { PermissionAction, PermissionItem } from "../model/Permissions";

const PROJECTS_ENABLED = "ec6b90b3-fcc3-4ed3-945f-422e9e8c40cf";

export default defineComponent({
  name: "CrmContactEmail",
  props: {
    visible: Boolean,
  },
  emits: ["update:visible"],
  setup(props, { emit }) {
    const year = new Date().getFullYear();
    const appVersion = computed(() => {
      return VersionHelper.getAppVersion();
    });
    const router = useRouter();
    const isProjectMenuVisible = ref(false);

    const isMenuVisible = computed({
      get(): boolean {
        return props.visible;
      },
      set(value: boolean) {
        emit("update:visible", value);
      },
    });

    /**
     * Gets the main path
     * @returns {string} main path
     */
    const mainPath = computed(() => {
      return router.currentRoute.value.name;
    });

    watch(
      () => props.visible,
      async (visible) => {
        if (visible) {
          await initProjectMenuVisibility();
        }
      },
      { immediate: true }
    );

    /**
     * Initializes the project menu visibility
     */
    async function initProjectMenuVisibility() {
      const hasProjectPermission = PermissionHelper.hasPermission({
        item: PermissionItem.PROJECT,
        action: PermissionAction.READ,
        userId: null,
      });
      if (!hasProjectPermission) {
        isProjectMenuVisible.value = false;
        return;
      }

      const [licenseResult, projectsEnabledResult] = await Promise.all([
        licenseApi.getMobiCrmLicense(),
        tenantSettingApi.getValue(PROJECTS_ENABLED),
      ]);
      isProjectMenuVisible.value =
        licenseResult.isSuccess &&
        licenseResult.data.level >= licenseLevel.ENTREPRISE &&
        projectsEnabledResult.isSuccess &&
        String(projectsEnabledResult.data) === "true";
    }

    /**
     * Logout user
     */
    async function logout() {
      close();
      await oidcManager.signOut();
      await router.push({ name: "welcome", query: { signedOut: "true" } });
    }

    /**
     * Closes sidebar
     */
    function close() {
      isMenuVisible.value = false;
    }

    return {
      year,
      appVersion,
      isMenuVisible,
      logout,
      close,
      mainPath,
      isProjectMenuVisible,
    };
  },
});
